import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Pagination schema mirroring common backend pattern
 */
export const PurchaseOrderPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Filters accepted by the list endpoint
 * All fields optional; server treats undefined as no filter
 */
export const ListPurchaseOrdersInputSchema = z.object({
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
  deliveryDateFrom: z.string().optional(),
  deliveryDateTo: z.string().optional(),
  status: z.array(z.string()).optional(),
  supplierId: UUIDSchema.optional(),
  salesOrderId: UUIDSchema.optional(),
  externalId: z.string().optional(),
  minTotalAmount: DecimalStringSchema.optional(),
  maxTotalAmount: DecimalStringSchema.optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * Purchase order status enum
 */
export const POStatusSchema = z.enum([
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "SENT",
  "PARTIALLY_RECEIVED",
  "FULLY_RECEIVED",
  "PARTIALLY_INVOICED",
  "FULLY_INVOICED",
  "CLOSED",
  "CANCELLED",
]);

/**
 * Base purchase order schema mirroring Sequelize attributes (sans line items)
 */
export const PurchaseOrderSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  internalId: z.int(),
  externalId: z.string(),
  supplierId: UUIDSchema,
  createdByUserId: UUIDSchema.nullable().optional(),
  salesOrderId: UUIDSchema.nullable().optional(),
  instructionId: UUIDSchema.nullable().optional(),
  shipmentId: UUIDSchema.nullable().optional(),
  orderDate: z.string(),
  deliveryDate: z.string().nullable().optional(),
  status: POStatusSchema,
  totalAmount: DecimalStringSchema.nullable().optional(),
  pdfS3Key: z.string().nullable().optional(),
  pdfS3Url: z.string().nullable().optional(),
  rawData: z.record(z.string(), z.unknown()).nullable().optional(),
  originId: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  originEntity: z.string().nullable().optional(),
  receivedFlag: z.boolean().nullable().optional(),
  currency: z.string().nullable().optional(),
  referenceId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

export const ListPurchaseOrdersResponseSchema = z.object({
  purchaseOrders: z.array(PurchaseOrderSchema),
  pagination: PurchaseOrderPaginationSchema,
});

export const GetPurchaseOrderInputSchema = z.object({
  id: UUIDSchema,
});

export const PurchaseOrderLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int(),
  productId: UUIDSchema.nullable().optional(),
  description: z.string().nullable().optional(),
  quantity: z.union([z.number(), z.string()]).nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  totalPrice: DecimalStringSchema.nullable().optional(),
  uom: z.string().nullable().optional(),
  expectedDeliveryDate: z.string().nullable().optional(),
  paymentHold: z.boolean(),
});

export const GetPurchaseOrderResponseSchema = z.object({
  purchaseOrder: PurchaseOrderSchema.nullable(),
  lineItems: z.array(PurchaseOrderLineItemSchema),
});

export const GetPurchaseOrderLineItemInputSchema = z.object({
  id: UUIDSchema,
});

export const GetPurchaseOrderLineItemResponseSchema = z.object({
  lineItem: PurchaseOrderLineItemSchema.nullable(),
  purchaseOrder: PurchaseOrderSchema.nullable(),
});

export const SearchPurchaseOrdersInputSchema = z.object({
  query: z.string().optional(),
  // Context for ranking
  invoiceId: UUIDSchema.optional(),
  supplierId: UUIDSchema.optional(),
  invoiceDate: z.string().optional(), // ISO date
  invoiceAmount: DecimalStringSchema.optional(),
  limit: z.int().min(1).max(50).optional(),
});

const SearchPurchaseOrderMatchedLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int().nullable(),
  description: z.string().nullable(),
});

export const SearchPurchaseOrderResultSchema = z.object({
  id: UUIDSchema,
  externalId: z.string(),
  internalId: z.int(),
  supplierId: UUIDSchema,
  supplierName: z.string(),
  orderDate: z.string(),
  status: POStatusSchema,
  totalAmount: DecimalStringSchema.nullable(),
  matchedLineItems: z
    .array(SearchPurchaseOrderMatchedLineItemSchema)
    .optional(),
  relevanceScore: z.number(),
});

export const SearchPurchaseOrdersResponseSchema = z.object({
  purchaseOrders: z.array(SearchPurchaseOrderResultSchema),
});

export const purchaseOrdersContracts = {
  list: oc
    .input(ListPurchaseOrdersInputSchema)
    .output(ListPurchaseOrdersResponseSchema)
    .route({
      method: "GET",
    }),
  get: oc
    .input(GetPurchaseOrderInputSchema)
    .output(GetPurchaseOrderResponseSchema)
    .route({
      method: "GET",
    }),
  getLineItem: oc
    .input(GetPurchaseOrderLineItemInputSchema)
    .output(GetPurchaseOrderLineItemResponseSchema)
    .route({
      method: "GET",
    }),
  search: oc
    .input(SearchPurchaseOrdersInputSchema)
    .output(SearchPurchaseOrdersResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;
export type PurchaseOrderLineItem = z.infer<typeof PurchaseOrderLineItemSchema>;
export type ListPurchaseOrdersInput = z.infer<
  typeof ListPurchaseOrdersInputSchema
>;
export type ListPurchaseOrdersResponse = z.infer<
  typeof ListPurchaseOrdersResponseSchema
>;
export type GetPurchaseOrderInput = z.infer<typeof GetPurchaseOrderInputSchema>;
export type GetPurchaseOrderResponse = z.infer<
  typeof GetPurchaseOrderResponseSchema
>;
export type GetPurchaseOrderLineItemInput = z.infer<
  typeof GetPurchaseOrderLineItemInputSchema
>;
export type GetPurchaseOrderLineItemResponse = z.infer<
  typeof GetPurchaseOrderLineItemResponseSchema
>;
export type SearchPurchaseOrdersInput = z.infer<
  typeof SearchPurchaseOrdersInputSchema
>;
export type SearchPurchaseOrdersResponse = z.infer<
  typeof SearchPurchaseOrdersResponseSchema
>;
export type SearchPurchaseOrderResult = z.infer<
  typeof SearchPurchaseOrderResultSchema
>;
export type POStatus = z.infer<typeof POStatusSchema>;
